/**
 * Dinamik rejim: bo'lakli (cell) model, RK4 integratsiya.
 *
 * MODEL
 *   Apparat uzunligi bo'ylab N bo'lakka bo'linadi. Har bo'lakda uch tugun:
 *   qobiq suyuqligi, quvur devori, quvur suyuqligi. Har biri "ideal aralashgan"
 *   (CSTR-in-series): bo'lakdan chiqish harorati = bo'lak harorati.
 *
 *   Qobiq bo'lagi j:   ms·cp(Ts) dTs/dt = Ws·[h(Ts,in) − h(Ts)] − Σ hs'·Acell·(Ts − Tw,k)
 *   Devor bo'lagi k:   mw·cpw  dTw/dt = hs'·Acell·(Ts,j(k) − Tw) − ht'·Acell·(Tw − Tt)
 *   Quvur bo'lagi k:   mt·cp(Tt) dTt/dt = Wt·[h(Tt,in) − h(Tt)] + ht'·Acell·(Tw − Tt)
 *
 *   hs', ht' — devor qarshiligining yarmi qo'shilgan film koeffitsientlari,
 *   1/hs' + 1/ht' = 1/U. 'given' rejimida hs' = ht' = 2U (ASSUMPTION: simmetrik).
 *
 *   shell-1-2 uchun quvur suyuqligi 2N bo'lak: birinchi N — qobiq bilan bir yo'nalishda,
 *   ikkinchi N — qarshi. Qobiq bo'lagi j ikkala yo'lning j-pozitsiyadagi bo'laklari bilan
 *   almashadi (qobiq suyuqligi kesimda to'liq aralashgan — ε-NTU formula bilan bir xil faraz).
 *
 * QABUL QILINGAN SODDALASHTIRISHLAR
 *   1. Suyuqlik massasi bo'lakda o'zgarmas (kirish haroratidagi zichlik; siqilmas).
 *   2. Oqim bo'ylab issiqlik o'tkazuvchanlik (axial conduction) e'tiborga olinmagan.
 *   3. Devor radial yo'nalishda bir tugunli (qalinligi kichik).
 *   4. Atrofga yo'qotish yo'q.
 *   5. N → ∞ da model plug-flow ε-NTU yechimiga yaqinlashadi; chekli N da
 *      sonli "diffuziya" bor: N = 20 da Q ≈ 1–3% kam (test bilan tasdiqlangan).
 *
 * ENERGIYA BALANSI (tekshiruv): ∫Wh·Δh dt − ∫Wc·Δh dt = ΔE(saqlangan). Kumulyativ
 * integrallar holat vektoriga qo'shilgan va bir xil RK4 bilan integratsiya qilinadi.
 */
import { density, enthalpyChange, heatCapacity } from '../../properties';
import { stepWithSubsteps } from '../../solvers/rk4';
import type { StepResult, Warning } from '../../types';
import {
  heatTransferArea,
  shellHoldupVolume,
  tubeHoldupVolume,
  tubePasses,
  validateParams,
  wallMass,
  wallResistance,
} from './geometry';
import { computeU, validateInputs } from './steady';
import type {
  HeatExchangerDynamicOutputs,
  HeatExchangerInputs,
  HeatExchangerParams,
  HeatExchangerState,
  Stream,
} from './types';

export const DEFAULT_CELLS = 20;
const T_REF = 273.15;

const g = (a: readonly number[], i: number): number => a[i] as number;

/** Boshlang'ich holat: barcha tugunlar `T0` da (standart — sovuq oqim kirishi). */
export function initState(
  p: HeatExchangerParams,
  inputs: HeatExchangerInputs,
  T0?: number,
): HeatExchangerState {
  validateParams(p);
  validateInputs(inputs);
  const N = p.cells ?? DEFAULT_CELLS;
  const M = N * tubePasses(p.arrangement);
  const T = T0 ?? inputs.cold.Tin;
  const state: HeatExchangerState = {
    t: 0,
    shell: new Array<number>(N).fill(T),
    tube: new Array<number>(M).fill(T),
    wall: new Array<number>(M).fill(T),
    energyFromHot: 0,
    energyToCold: 0,
    storedEnergy0: 0,
  };
  state.storedEnergy0 = storedEnergy(p, inputs, state);
  return state;
}

interface Sides {
  shellStream: Stream;
  tubeStream: Stream;
  hotIsShell: boolean;
}

function sides(p: HeatExchangerParams, inputs: HeatExchangerInputs): Sides {
  const hotIsShell = p.hotSide === 'shell';
  return {
    shellStream: hotIsShell ? inputs.hot : inputs.cold,
    tubeStream: hotIsShell ? inputs.cold : inputs.hot,
    hotIsShell,
  };
}

/** Qobiq bo'lagi indeksi, quvur bo'lagi k uchun */
function shellIndexOf(p: HeatExchangerParams, N: number, k: number): number {
  if (p.arrangement === 'parallel') return k;
  if (p.arrangement === 'counter') return N - 1 - k;
  // shell-1-2: birinchi yo'l to'g'ri, ikkinchi teskari
  return k < N ? k : 2 * N - 1 - k;
}

function holdupMasses(p: HeatExchangerParams, s: Sides, N: number, M: number) {
  const rhoS = density(
    s.shellStream.substance,
    s.shellStream.phase,
    s.shellStream.Tin,
    s.shellStream.pressure,
  );
  const rhoT = density(
    s.tubeStream.substance,
    s.tubeStream.phase,
    s.tubeStream.Tin,
    s.tubeStream.pressure,
  );
  return {
    mShell: (rhoS * shellHoldupVolume(p)) / N,
    mTube: (rhoT * tubeHoldupVolume(p)) / M,
    mWall: wallMass(p) / M,
  };
}

/** Saqlangan energiya T_REF ga nisbatan, J */
export function storedEnergy(
  p: HeatExchangerParams,
  inputs: HeatExchangerInputs,
  state: HeatExchangerState,
): number {
  const s = sides(p, inputs);
  const N = state.shell.length;
  const M = state.tube.length;
  const { mShell, mTube, mWall } = holdupMasses(p, s, N, M);
  let E = 0;
  for (const T of state.shell)
    E += mShell * enthalpyChange(s.shellStream.substance, s.shellStream.phase, T_REF, T);
  for (const T of state.tube)
    E += mTube * enthalpyChange(s.tubeStream.substance, s.tubeStream.phase, T_REF, T);
  for (const T of state.wall) E += mWall * p.tubes.wallCp * (T - T_REF);
  return E;
}

/** hs', ht' — devor qarshiligi teng bo'lingan film koeffitsientlari */
function splitCoefficients(
  p: HeatExchangerParams,
  inputs: HeatExchangerInputs,
  ThMean: number,
  TcMean: number,
  warnings: Warning[],
): { hShell: number; hTube: number; U: number } {
  const u = computeU(p, inputs, ThMean, TcMean, warnings);
  if (p.uMode === 'given' || u.hInner === null || u.hOuter === null) {
    return { hShell: 2 * u.U, hTube: 2 * u.U, U: u.U };
  }
  const Rw = wallResistance(p);
  const ratio = p.tubes.outerDiameter / p.tubes.innerDiameter;
  const hShell = 1 / (1 / u.hOuter + (p.foulingOuter ?? 0) + Rw / 2);
  const hTube = 1 / (ratio * (1 / u.hInner + (p.foulingInner ?? 0)) + Rw / 2);
  return { hShell, hTube, U: u.U };
}

function mean(a: readonly number[]): number {
  let s = 0;
  for (const v of a) s += v;
  return s / a.length;
}

/**
 * Bitta vaqt qadami. Ichki qadam barqarorlik uchun avtomatik bo'linadi.
 * Kirishlar (sarf, harorat) har qadamda o'zgarishi mumkin — talaba ventilni buraydi.
 */
export function stepDynamic(
  p: HeatExchangerParams,
  state: HeatExchangerState,
  inputs: HeatExchangerInputs,
  dt: number,
): StepResult<HeatExchangerState, HeatExchangerDynamicOutputs> {
  validateInputs(inputs);
  if (!(dt > 0)) throw new Error('heat-exchanger: dt must be > 0');
  const warnings: Warning[] = [];
  const s = sides(p, inputs);
  const N = state.shell.length;
  const M = state.tube.length;
  const Acell = heatTransferArea(p) / M;
  const { mShell, mTube, mWall } = holdupMasses(p, s, N, M);
  const cpw = p.tubes.wallCp;

  const ThMean = mean(s.hotIsShell ? state.shell : state.tube);
  const TcMean = mean(s.hotIsShell ? state.tube : state.shell);
  const { hShell, hTube, U } = splitCoefficients(p, inputs, ThMean, TcMean, warnings);

  const Ws = s.shellStream.massFlow;
  const Wt = s.tubeStream.massFlow;
  const hS = (T: number) => enthalpyChange(s.shellStream.substance, s.shellStream.phase, T_REF, T);
  const hT = (T: number) => enthalpyChange(s.tubeStream.substance, s.tubeStream.phase, T_REF, T);
  const cpS = (T: number) => heatCapacity(s.shellStream.substance, s.shellStream.phase, T);
  const cpT = (T: number) => heatCapacity(s.tubeStream.substance, s.tubeStream.phase, T);
  const hSin = hS(s.shellStream.Tin);
  const hTin = hT(s.tubeStream.Tin);
  const shellOf = Array.from({ length: M }, (_, k) => shellIndexOf(p, N, k));

  // Holat vektori: [shell(N), tube(M), wall(M), Ehot, Ecold]
  const iT = N;
  const iW = N + M;
  const iEh = N + 2 * M;
  const iEc = iEh + 1;

  const deriv = (_t: number, y: readonly number[]): number[] => {
    const dy = new Array<number>(y.length).fill(0);
    // Qobiq → devor oqimlari
    const qShellToWall = new Array<number>(N).fill(0);
    let Qs = 0;
    let Qt = 0;
    for (let k = 0; k < M; k++) {
      const j = g(shellOf, k);
      const Ts = g(y, j);
      const Tw = g(y, iW + k);
      const Tt = g(y, iT + k);
      const qsw = hShell * Acell * (Ts - Tw);
      const qwt = hTube * Acell * (Tw - Tt);
      qShellToWall[j] = g(qShellToWall, j) + qsw;
      dy[iW + k] = (qsw - qwt) / (mWall * cpw);
      const TtIn = k === 0 ? s.tubeStream.Tin : g(y, iT + k - 1);
      const hIn = k === 0 ? hTin : hT(TtIn);
      dy[iT + k] = (Wt * (hIn - hT(Tt)) + qwt) / (mTube * cpT(Tt));
      Qs += qsw;
      Qt += qwt;
    }
    for (let j = 0; j < N; j++) {
      const Ts = g(y, j);
      const TsIn = j === 0 ? s.shellStream.Tin : g(y, j - 1);
      const hIn = j === 0 ? hSin : hS(TsIn);
      dy[j] = (Ws * (hIn - hS(Ts)) - g(qShellToWall, j)) / (mShell * cpS(Ts));
    }
    // Kumulyativ energiya: issiq oqim bergan / sovuq oqim olgan (entalpiya bo'yicha)
    const shellNet = Ws * (hSin - hS(g(y, N - 1)));
    const tubeNet = Wt * (hTin - hT(g(y, iT + M - 1)));
    dy[iEh] = s.hotIsShell ? shellNet : tubeNet;
    dy[iEc] = s.hotIsShell ? -tubeNet : -shellNet;
    void Qs;
    void Qt;
    return dy;
  };

  // Barqarorlik: eng kichik vaqt doimiysi
  const tauShell =
    (mShell * cpS(s.shellStream.Tin)) / (Ws * cpS(s.shellStream.Tin) + hShell * Acell * (M / N));
  const tauTube = (mTube * cpT(s.tubeStream.Tin)) / (Wt * cpT(s.tubeStream.Tin) + hTube * Acell);
  const tauWall = (mWall * cpw) / ((hShell + hTube) * Acell);
  const maxSub = 0.4 * Math.min(tauShell, tauTube, tauWall);

  const y0 = [
    ...state.shell,
    ...state.tube,
    ...state.wall,
    state.energyFromHot,
    state.energyToCold,
  ];
  const y1 = stepWithSubsteps(deriv, state.t, y0, dt, maxSub);

  const next: HeatExchangerState = {
    t: state.t + dt,
    shell: y1.slice(0, N),
    tube: y1.slice(iT, iT + M),
    wall: y1.slice(iW, iW + M),
    energyFromHot: g(y1, iEh),
    energyToCold: g(y1, iEc),
    storedEnergy0: state.storedEnergy0,
  };

  // Oniy oqimlar (yangi holatda)
  let Qhot = 0;
  let Qcold = 0;
  for (let k = 0; k < M; k++) {
    const j = g(shellOf, k);
    const qsw = hShell * Acell * (g(next.shell, j) - g(next.wall, k));
    const qwt = hTube * Acell * (g(next.wall, k) - g(next.tube, k));
    if (s.hotIsShell) {
      Qhot += qsw;
      Qcold += qwt;
    } else {
      Qhot -= qwt;
      Qcold -= qsw;
    }
  }

  const shellOut = g(next.shell, N - 1);
  const tubeOut = g(next.tube, M - 1);
  const stored = storedEnergy(p, inputs, next) - next.storedEnergy0;
  const balanceError =
    (next.energyFromHot - next.energyToCold - stored) / Math.max(Math.abs(next.energyFromHot), 1);

  for (const T of [...next.shell, ...next.tube]) {
    if (!Number.isFinite(T))
      throw new Error('heat-exchanger: non-finite temperature (unstable step)');
  }

  return {
    state: next,
    outputs: {
      t: next.t,
      hotTout: s.hotIsShell ? shellOut : tubeOut,
      coldTout: s.hotIsShell ? tubeOut : shellOut,
      Qhot,
      Qcold,
      U,
      energyFromHot: next.energyFromHot,
      energyToCold: next.energyToCold,
      storedEnergyDelta: stored,
      balanceError,
    },
    warnings,
  };
}
